import React from 'react';
import { useSelector } from 'react-redux';
import { Icon, Modal } from 'semantic-ui-react';
import UserContainer from 'components/user/container/UserContainer';
import { Header } from 'components/atoms/Header/Header';
import Table from 'components/table/Table';
import { formatDate, UTCDateToLocal } from 'controls/utils';
import { Button } from 'components/atoms/Button';
import InlineSpinner from 'components/atoms/Spinner/InlineSpinner';
import { api } from 'api';
import moment from 'moment';
import { subscriptionDetailsStrings } from './strings';

const SubscriptionDetails = () => {
  const userSub = useSelector((state) => state.subscription);
  const siteSubscription = useSelector((state) => state.siteInfo?.subscription);
  const entitlement = useSelector((state) => state.entitlement);

  const [action, setAction] = React.useState(/** @type {'cancel' | 'pause' | 'resume' | null} */ (null));
  const [loadingPauseAction, setLoadingPauseAction] = React.useState(false);

  async function togglePause() {
    setLoadingPauseAction(true);

    try {
      if (action === 'pause') {
        await api.pauseSub();
      } else {
        await api.resumeSub();
      }
    } catch (e) {
      console.error(e);
    }

    setAction(null);
    setLoadingPauseAction(false);
  }

  const cancelMessage = (
    <p>
      Are you sure you want to cancel your subscription? You will still have access to it until
      {' '}
      {userSub?.nextRenewal ? UTCDateToLocal(userSub.nextRenewal) : ''}
      .
    </p>
  );

  const cancelLink = (
    <a
      href={userSub?.cancelUrl || ''}
      className='main-btn'
      style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}
    >
      Cancel my subscription
    </a>
  );

  const pauseMessage = (
    <p>
      Are you sure you want to pause your subscription?
    </p>
  );

  const togglePauseLink = (
    <Button disabled={loadingPauseAction} onClick={() => togglePause()} text={loadingPauseAction ? <InlineSpinner /> : action === 'resume' ? 'Resume my subscription' : 'Pause my subscription'} />
  );

  const resumeMessage = (
    <p>
      Are you sure you want to resume your subscription?
    </p>
  );

  const modalMessage = action === 'cancel' ? cancelMessage : action === 'pause' ? pauseMessage : resumeMessage;
  const modalLink = action === 'cancel' ? cancelLink : togglePauseLink;

  const renewalDatePassed = userSub && moment().isAfter(userSub.nextRenewal);

  const hasScheduledPaused = userSub && userSub.pausedFrom;
  const isPaused = hasScheduledPaused && renewalDatePassed;

  const hasScheduledCancell = userSub && userSub.status === 'Cancelled';
  const isCancelled = hasScheduledCancell && renewalDatePassed;

  let statusMessage = `and is currently ${userSub?.status}`;
  if (hasScheduledPaused) statusMessage = `and will be Paused on ${formatDate(userSub.nextRenewal)}`;
  if (hasScheduledCancell) statusMessage = `and will be Canceled on ${formatDate(userSub.nextRenewal)}`;

  let statusValue = 'Active';
  if (isPaused) statusValue = 'Paused';
  if (isCancelled) statusValue = 'Cancelled';

  return (
    <UserContainer>
      <div style={{ width: '100%' }}>
        <Modal open={Boolean(action)} onClose={() => setAction(null)} closeOnDimmerClick>
          <Modal.Header icon='question' content={modalMessage} />
          <Modal.Content>
            {modalLink}
            <Button text='Close' onClick={() => setAction(null)} />
          </Modal.Content>
        </Modal>
        {userSub
          ? (
            <>
              <p>{`Your subscription ${subscriptionDetailsStrings.english.placed} ${formatDate(userSub.startDate)} ${statusMessage}.`}</p>
              <Header text={subscriptionDetailsStrings.english.title} />
              <Table>
                <Table.Content>
                  <>
                    <div className='table-row'>
                      <div className='table-content__item table-item__long'>
                        <p>{entitlement?.name || 'WalkMix Creator'}</p>
                        <p>{!entitlement && 'No entitlement present.'}</p>
                      </div>
                      <div className='table-content__item table-item__long'>
                        <h3>{siteSubscription?.prices?.gross}</h3>
                      </div>
                    </div>
                    <div className='table-row'>
                      <div className='table-content__item'>
                        <h3>{subscriptionDetailsStrings.english.pay}</h3>
                      </div>
                      <div className='table-content__item'>
                        <h3>PADDLE</h3>
                      </div>
                    </div>
                    <div className='table-row'>
                      <div className='table-content__item'>
                        <h3>{subscriptionDetailsStrings.english.TOTAL}</h3>
                      </div>
                      <div className='table-content__item'>
                        <h3>{siteSubscription?.prices?.gross}</h3>
                      </div>
                    </div>
                    <div className='table-row'>
                      <div className='table-content__item'>
                        <h3>Status:</h3>
                      </div>
                      <div className='table-content__item'>
                        <h3>{statusValue}</h3>
                      </div>
                    </div>
                    <div className='table-row'>
                      <div className='table-content__item'>
                        <h3>Start date:</h3>
                      </div>
                      <div className='table-content__item'>
                        <h3>{UTCDateToLocal(userSub.startDate)}</h3>
                      </div>
                    </div>
                    <div className='table-row'>
                      <div className='table-content__item'>
                        <h3>{hasScheduledPaused || hasScheduledCancell ? 'Active until:' : 'Next renewal date:'}</h3>
                      </div>
                      <div className='table-content__item'>
                        <h3>{UTCDateToLocal(userSub.nextRenewal)}</h3>
                      </div>
                    </div>
                  </>
                </Table.Content>
              </Table>
              <Header text={subscriptionDetailsStrings.english.info} />
              <div style={{ buserSub: '1px solid white', padding: '10px' }}>
                <p>{entitlement?.user?.name}</p>
                <p>
                  <Icon name='mail' size='small' />
                  {entitlement?.user?.email}
                </p>
              </div>
              {
                userSub?.status !== 'Cancelled' && (
                  <>
                    <Header text={subscriptionDetailsStrings.english.cancelTitle} />
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                      <Button text={subscriptionDetailsStrings.english.cancel} onClick={() => setAction('cancel')} />
                      <Button
                        disabled={loadingPauseAction}
                        text={
                              loadingPauseAction ? <InlineSpinner />
                                : hasScheduledPaused
                                  ? subscriptionDetailsStrings.english.resume
                                  : subscriptionDetailsStrings.english.pause
                            }
                        onClick={() => setAction(hasScheduledPaused ? 'resume' : 'pause')}
                      />
                    </div>
                  </>
                )
              }
            </>
          )
          : (<p>No subscription has been made yet.</p>)}
      </div>
    </UserContainer>
  );
};

export default SubscriptionDetails;
